import React, { useState, useEffect } from 'react';
import {
    deleteSubmission,
    updateSubmissionStatus,
    getSubmissionStatuses,
    searchSubmissions, // Added for pagination support
} from '../api/submissionapi';
import { getFormById } from '../api/formapi';
import moment from 'moment';
import Layout from './container/Layout';

const SubmissionList = ({ formId = null }) => {
    // States for submission list
    const [submissions, setSubmissions] = useState([]);
    const [filteredSubmissions, setFilteredSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formDetails, setFormDetails] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');

    // States for modal/popup
    const [showModal, setShowModal] = useState(false);
    const [currentSubmission, setCurrentSubmission] = useState(null);

    // States for pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const submissionStatuses = getSubmissionStatuses();

    // Fetch submissions with pagination (limit = 5)
    const fetchSubmissions = async (pageToFetch = 1, filter = statusFilter) => {
        try {
            setLoading(true);
            const params = { limit: 5, page: pageToFetch };
            if (formId) {
                params.formId = formId;
            }
            // Pass filter to backend if not 'all'
            if (filter !== 'all') {
                params.status = filter;
            }
            const response = await searchSubmissions(params);
            const { submissions: newSubs, pagination } = response;
            if (pageToFetch === 1) {
                setSubmissions(newSubs);
            } else {
                setSubmissions(prev => [...prev, ...newSubs]);
            }
            setPage(pageToFetch);
            setTotalPages(pagination.pages);
            setError('');
            // Fetch form details only on first page if formId provided
            if (formId && pageToFetch === 1) {
                const form = await getFormById(formId);
                setFormDetails(form);
            }
        } catch (err) {
            setError('Failed to fetch submissions');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubmissions(1, statusFilter);
    }, [formId, statusFilter]);

    // Apply status filter when submissions or statusFilter changes
    useEffect(() => {
        if (statusFilter === 'all') {
            setFilteredSubmissions(submissions);
        } else {
            setFilteredSubmissions(
                submissions.filter(submission => submission.status === statusFilter)
            );
        }
    }, [submissions, statusFilter]);

    // Handle submission deletion
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this submission?')) {
            try {
                await deleteSubmission(id);
                setSubmissions(submissions.filter(submission => submission._id !== id));
            } catch (err) {
                setError('Failed to delete submission');
                console.error(err);
            }
        }
    };

    // Handle status update
    const handleStatusChange = async (id, newStatus) => {
        try {
            const updatedSubmission = await updateSubmissionStatus(id, newStatus);
            setSubmissions(submissions.map(sub =>
                sub._id === id ? updatedSubmission : sub
            ));
        } catch (err) {
            setError('Failed to update submission status');
            console.error(err);
        }
    };

    // Handle status filter change
    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
    };

    // Open modal for viewing submission details
    const handleViewSubmission = (submission) => {
        setCurrentSubmission(submission);
        setShowModal(true);
    };

    // Format submission data for display
    const formatSubmissionData = (data, fields = []) => {
        if (!data || typeof data !== 'object') return 'No data';
        return (
            <div className="space-y-2">
                {fields.length > 0 ? (
                    fields.map(field => (
                        <div key={field.fieldName} className="flex">
                            <span className="font-medium text-gray-700 w-32 flex-shrink-0">
                                {field.fieldName}:
                            </span>
                            <span className="text-gray-900">
                                {data[field.fieldName] || 'N/A'}
                            </span>
                        </div>
                    ))
                ) : (
                    Object.entries(data).map(([key, value]) => (
                        <div key={key} className="flex">
                            <span className="font-medium text-gray-700 w-32 flex-shrink-0">
                                {key}:
                            </span>
                            <span className="text-gray-900">
                                {typeof value === 'object' ? JSON.stringify(value) : value}
                            </span>
                        </div>
                    ))
                )}
            </div>
        );
    };


    // Format date
    const formatDate = (timestamp) => {
        return moment(timestamp * 1000).format('MMM D, YYYY h:mm A');
    };

    // Get status badge styling
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'approved':
                return 'bg-green-100 text-green-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            case 'suspicious':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Modal component
    const SubmissionModal = () => {
        if (!currentSubmission) return null;
    
        // Helper function to format field value based on its type
        const formatFieldValue = (value, fieldType) => {
            // If the value is null or undefined, return a dash
            if (value === null || value === undefined) return '—';
    
            // Handle different field types
            switch (fieldType) {
                case 'checkbox':
                    // For boolean values from checkboxes
                    return typeof value === 'boolean' 
                        ? (value ? 'Yes' : 'No')
                        : (value === 'true' || value === true ? 'Yes' : 'No');
                case 'date':
                    // Format date values nicely
                    try {
                        return new Date(value).toLocaleDateString();
                    } catch (e) {
                        return value;
                    }
                default:
                    // Return other values as strings
                    return String(value);
            }
        };
    
        // Enhanced function to format submission data with field type awareness
        const enhancedFormatSubmissionData = (data, fields = []) => {
            if (!data || Object.keys(data).length === 0) {
                return <p className="text-sm text-gray-500 italic">No data submitted</p>;
            }
    
            // Create a map of field names to their types for quick lookup
            const fieldTypeMap = {};
            if (fields && fields.length > 0) {
                fields.forEach(field => {
                    fieldTypeMap[field.fieldName] = field.fieldType;
                });
            }
    
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(data).map(([key, value], index) => {
                        // Determine field type from the map, default to 'text'
                        const fieldType = fieldTypeMap[key] || 'text';
                        const formattedValue = formatFieldValue(value, fieldType);
                        
                        return (
                            <div key={index} className="border-b border-gray-200 pb-3">
                                <p className="text-sm font-medium text-gray-700 mb-1">{key}</p>
                                <div className="text-sm">
                                    {fieldType === 'checkbox' ? (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${formattedValue === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {formattedValue}
                                        </span>
                                    ) : (
                                        <p className="text-gray-900 break-words">{formattedValue}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        };
    
        return (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 flex items-center justify-center">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-lg font-medium text-gray-900">
                            Submission Details
                        </h3>
                        <button
                            type="button"
                            className="text-gray-400 hover:text-gray-500 focus:outline-none"
                            onClick={() => setShowModal(false)}
                        >
                            <span className="sr-only">Close</span>
                            <svg
                                className="h-6 w-6"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
    
                    <div className="px-6 py-4 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                                    Basic Information
                                </h4>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Submission ID</p>
                                        <p className="text-sm font-mono text-gray-900">
                                            {currentSubmission._id}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Form ID</p>
                                        <p className="text-sm font-mono text-gray-900">
                                            {currentSubmission.formId}
                                        </p>
                                    </div>
                                    {formDetails && formDetails.formName && (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Form Name</p>
                                            <p className="text-sm text-gray-900">
                                                {formDetails.formName}
                                            </p>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Status</p>
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                                                currentSubmission.status
                                            )}`}
                                        >
                                            {currentSubmission.status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                                    Timeline
                                </h4>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Created At</p>
                                        <p className="text-sm text-gray-900">
                                            {formatDate(currentSubmission.createdAt)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Updated At</p>
                                        <p className="text-sm text-gray-900">
                                            {formatDate(currentSubmission.updatedAt)}
                                        </p>
                                    </div>
                                    {currentSubmission.metadata && (
                                        <div className="bg-gray-50 p-4 rounded-md mt-4">
                                            <h5 className="text-sm font-medium text-gray-700 mb-2">
                                                Metadata
                                            </h5>
                                            {/* Display all metadata fields */}
                                            <div className="space-y-2 text-sm">
                                                {currentSubmission.metadata.formId && (
                                                    <div className="flex gap-2 items-center">
                                                        <span className="text-gray-500 w-24">Form ID:</span>
                                                        <span className="text-gray-900 font-mono">{currentSubmission.metadata.formId}</span>
                                                    </div>
                                                )}
                                                {currentSubmission.metadata.formName && (
                                                    <div className="flex gap-2 items-center">
                                                        <span className="text-gray-500 w-24">Form Name:</span>
                                                        <span className="text-gray-900">{currentSubmission.metadata.formName}</span>
                                                    </div>
                                                )}
                                                {currentSubmission.metadata.ip && (
                                                    <div className="flex gap-2 items-center">
                                                        <span className="text-gray-500 w-24">IP Address:</span>
                                                        <span className="text-gray-900 font-mono">{currentSubmission.metadata.ip}</span>
                                                    </div>
                                                )}
                                            </div>
                                            {currentSubmission.status === 'suspicious' &&
                                                currentSubmission.metadata.suspiciousReason && (
                                                    <div className="mt-2 p-3 bg-red-50 border-l-4 border-red-400 rounded-md">
                                                        <span className="text-sm font-medium text-red-800">
                                                            Suspicious Reason:
                                                        </span>
                                                        <p className="mt-1 text-sm text-red-700">
                                                            {currentSubmission.metadata.suspiciousReason}
                                                        </p>
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div>
                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                                Form Data
                            </h4>
                            <div className="bg-gray-50 p-4 rounded-md border border-gray-200 overflow-x-auto">
                                {enhancedFormatSubmissionData(
                                    currentSubmission.data,
                                    formDetails?.fields
                                )}
                            </div>
                        </div>
                        {currentSubmission.unregisteredFields &&
                            Object.keys(currentSubmission.unregisteredFields).length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                                        Unregistered Fields
                                    </h4>
                                    <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 overflow-x-auto">
                                        {enhancedFormatSubmissionData(
                                            currentSubmission.unregisteredFields
                                        )}
                                    </div>
                                </div>
                            )}
                        <div className="pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                                Update Status
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {submissionStatuses.map(status => (
                                    <button
                                        key={status.value}
                                        className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${currentSubmission.status === status.value
                                            ? 'bg-gray-100 text-gray-800 cursor-default'
                                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-blue-500'
                                            }`}
                                        onClick={() => {
                                            handleStatusChange(currentSubmission._id, status.value);
                                            setCurrentSubmission({
                                                ...currentSubmission,
                                                status: status.value
                                            });
                                        }}
                                        disabled={currentSubmission.status === status.value}
                                    >
                                        {status.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Render content based on loading/error states and the filtered submissions
    const renderContent = () => {
        if (loading && page === 1) {
            return (
                <div className="flex justify-center my-12">
                    <svg
                        className="animate-spin h-8 w-8 text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        ></circle>
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                </div>
            );
        }

        if (error) {
            return (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-red-500"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <>
                {filteredSubmissions.length === 0 ? (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="p-8 text-center">
                            <svg
                                className="mx-auto h-12 w-12 text-gray-400"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">
                                No submissions found
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                                No submissions match your current filter.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto shadow rounded-lg">
                        <div className="max-h-[520px] overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            Form ID
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            Data Preview
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            Status
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            Created At
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredSubmissions.map(submission => (
                                        <tr key={submission._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {submission.formId}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-xs">
                                                    {
                                                        // check if submission.data not empty
                                                        (submission.data &&
                                                            Object.keys(submission.data).length > 0) &&
                                                        Object.keys(submission.data)
                                                            .slice(0, 2)
                                                            .map(key => {

                                                                if (!key) return "No Data available";
                                                                return (
                                                                    <div key={key} className="text-sm truncate mb-1">
                                                                        <span className="font-medium text-gray-900">
                                                                            {key}:
                                                                        </span>{' '}
                                                                        <span className="text-gray-500">
                                                                            {typeof submission.data[key] === 'object'
                                                                                ? 'Complex data'
                                                                                : String(submission.data[key]).substring(0, 30)}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            })}
                                                    {(submission.data && Object.keys(submission.data).length > 0) ?
                                                        Object.keys(submission.data).length > 2 && (
                                                            <div className="text-xs text-gray-400 italic">
                                                                More fields available...
                                                            </div>
                                                        ) : <div className="text-xs text-gray-400 italic">
                                                            No data available
                                                        </div>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                                                        submission.status
                                                    )}`}
                                                >
                                                    {submission.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(submission.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleViewSubmission(submission)}
                                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                                >
                                                    View
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(submission._id)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {/* Load More Button */}
                {page < totalPages && !loading && (
                    <div className="flex justify-center">
                        <button
                            onClick={() => fetchSubmissions(page + 1)}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Load More
                        </button>
                    </div>
                )}
            </>
        );
    };

    return (
        <Layout>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                        {formDetails ? `Submissions for ${formDetails.formName}` : 'All Submissions'}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        View and manage form submissions
                    </p>
                </div>
                <div className="mt-3 sm:mt-0 flex items-center">
                    <label
                        htmlFor="status-filter"
                        className="mr-2 text-sm font-medium text-gray-700"
                    >
                        Filter by Status:
                    </label>
                    <select
                        id="status-filter"
                        value={statusFilter}
                        onChange={(e) => handleStatusFilterChange(e.target.value)}
                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                        <option value="all">All Statuses</option>
                        {submissionStatuses.map(status => (
                            <option key={status.value} value={status.value}>
                                {status.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {renderContent()}

            {showModal && <SubmissionModal />}
        </Layout>
    );
};

export default SubmissionList;
